import React, { useState, useEffect, useMemo, useCallback } from "react";
import Button from "../../components/Button/Button";
import {
  getValidationBadgesByUser,
  addValidationBadge,
  deleteValidationBadge, // 🆕 besoin côté services
  toAbsoluteBadgeUrl,
} from "../../services/validationBadgeServices";
import { getClubs } from "../../services/clubServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import { Modal } from "../../components/Modal/Modal"; // 🆕 modale d'aide

const ILLUSTRATION = "/img/luttecontrelesderives.png";

export default function ValidationBadge({ user, addNotification }) {
  const userId = user?.userId || user?.id;

  // Datas
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);

  // Clubs & filtres
  const [clubs, setClubs] = useState([]);
  const [selectedDepartement, setSelectedDepartement] = useState("");
  const [selectedClubId, setSelectedClubId] = useState("");

  // Form
  const [file, setFile] = useState(null);
  const [validationDate, setValidationDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // UI
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showHelp, setShowHelp] = useState(false); // 🆕 état modale d'aide

  /* =========================
     Fetch badges & clubs
     ========================= */
  const fetchBadges = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getValidationBadgesByUser(userId);
      setBadges(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Erreur chargement badges:", e);
      addNotification?.("Erreur lors du chargement des attestations.", "error");
    } finally {
      setLoading(false);
    }
  }, [userId, addNotification]);

  const fetchClubs = useCallback(async () => {
    try {
      const data = await getClubs();
      setClubs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Erreur chargement clubs:", e);
      addNotification?.("Erreur lors du chargement des clubs.", "error");
    }
  }, [addNotification]);

  useEffect(() => {
    fetchClubs();
    fetchBadges();
  }, [fetchClubs, fetchBadges]);

  /* =========================
     Sélecteurs Département / Club
     ========================= */
  const departements = useMemo(() => {
    const deps = clubs.map((c) => c.departement_club).filter(Boolean);
    return [...new Set(deps)].sort();
  }, [clubs]);

  const clubsFiltered = useMemo(() => {
    if (!selectedDepartement) return [];
    return clubs
      .filter((c) => c.departement_club === selectedDepartement)
      .sort((a, b) => a.nom_club.localeCompare(b.nom_club));
  }, [clubs, selectedDepartement]);

  useEffect(() => {
    // Reset du club si on change de département
    setSelectedClubId("");
  }, [selectedDepartement]);

  /* =========================
     Liste triée (récent → ancien)
     ========================= */
  const sortedBadges = useMemo(() => {
    const arr = Array.isArray(badges) ? [...badges] : [];
    return arr.sort(
      (a, b) => new Date(b.validation_date) - new Date(a.validation_date)
    );
  }, [badges]);

  /* =========================
     Handlers
     ========================= */
  const handleSubmitBadge = async (e) => {
    e.preventDefault();
    if (!file) return addNotification?.("Sélectionne un PDF.", "error");
    if (!validationDate)
      return addNotification?.("Indique la date de validité.", "error");
    if (!selectedClubId)
      return addNotification?.("Sélectionne un club.", "error");

    setSubmitting(true);
    try {
      const created = await addValidationBadge({
        user_id: userId,
        club_id: Number(selectedClubId),
        validation_date: validationDate,
        file,
      });

      if (created) {
        // Notification admin
        try {
          const selectedClub = clubs.find(
            (c) => String(c.id) === String(selectedClubId)
          );
          const clubName = selectedClub?.nom_club || "Club inconnu";
          const fullName = [user?.nom, user?.prenom].filter(Boolean).join(" ");
          const dateStr = new Date(validationDate).toLocaleDateString("fr-FR");
          const message = `${fullName} a ajouté une attestation (module éthique) validée le ${dateStr} — ${clubName}.`;
          await sendNotificationToAdmin(message, userId);
        } catch (notifyErr) {
          console.error("Erreur envoi notification admin:", notifyErr);
        }

        addNotification?.("Attestation ajoutée avec succès.", "success");
        // Reset form
        setFile(null);
        setValidationDate("");
        setSelectedDepartement("");
        setSelectedClubId("");
        setShowAddForm(false);
        fetchBadges();
      } else {
        addNotification?.("Échec de l'ajout de l’attestation.", "error");
      }
    } catch (err) {
      console.error(err);
      addNotification?.("Erreur lors de l'ajout de l’attestation.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!badgeId) return;
    const ok = window.confirm(
      "Supprimer cette attestation ? Cette action est irréversible."
    );
    if (!ok) return;

    try {
      setDeletingId(badgeId);
      await deleteValidationBadge(badgeId);
      addNotification?.("Attestation supprimée.", "success");
      fetchBadges();
    } catch (err) {
      console.error(err);
      addNotification?.("Erreur lors de la suppression.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const getBadgeFileName = (badge) => {
    return (
      badge?.original_filename ||
      badge?.filename ||
      (badge?.badge_url
        ? decodeURIComponent(
            (badge.badge_url.split("/").pop() || "").split("?")[0]
          )
        : "")
    );
  };

  const openPdf = (badgeUrl) => {
    const url = toAbsoluteBadgeUrl(badgeUrl);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  /* =========================
     UI
     ========================= */
  return (
    <section
      className="
        mt-8 rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70
        bg-white/80 dark:bg-slate-800/70 shadow-sm backdrop-blur
        overflow-hidden
      "
    >
      {/* Header */}
      <div
        className="
          px-5 py-4
          bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600
          dark:from-blue-700 dark:via-indigo-700 dark:to-violet-700
          text-white
        "
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold tracking-wide">
              Attestations de formation (enseignants / dirigeants)
            </h3>
            <p className="text-white/80 text-sm mt-1">
              Module « Éthique, déontologie et lutte contre les violences »
            </p>
          </div>

          {/* Actions header */}
          <div className="flex items-center gap-2">
            {/* 🆕 Bouton aide */}
            <Button
              text="Comment trouver ce document ?"
              onClick={() => setShowHelp(true)}
              variant="ghost"
              size="sm"
              className="text-white border-white/30 hover:bg-white/10 ring-white/40"
              hoverLabel="Où récupérer le PDF sur France Judo ?"
            />
            {/* CTA Ajouter (toujours visible) */}
            <Button
              text={showAddForm ? "Masquer le formulaire" : "Ajouter une attestation"}
              onClick={() => setShowAddForm((s) => !s)}
              variant="ghost"
              size="sm"
              className="text-white border-white/30 hover:bg-white/10 ring-white/40"
              hoverLabel={showAddForm ? "Masquer le formulaire" : "Ajouter une attestation"}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Loading */}
        {loading ? (
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Chargement…
          </div>
        ) : (
          <>
            {/* Liste des attestations */}
            {sortedBadges.length > 0 ? (
              <div className="space-y-3">
                {sortedBadges.map((badge) => {
                  const clubName =
                    badge?.Club?.nom_club || badge?.club?.nom_club || "";
                  const dateStr = badge?.validation_date
                    ? new Date(badge.validation_date).toLocaleDateString(
                        "fr-FR"
                      )
                    : "-";
                  const fileName = getBadgeFileName(badge);
                  return (
                    <div
                      key={badge.id}
                      className="
                        grid grid-cols-1 md:grid-cols-[96px,1fr,auto] gap-4
                        items-center p-3 rounded-xl
                        ring-1 ring-slate-200/70 dark:ring-slate-700/70
                        bg-slate-50/70 dark:bg-slate-900/40
                      "
                    >
                      {/* Vignette cliquable */}
                      <button
                        type="button"
                        onClick={() => openPdf(badge.badge_url)}
                        title="Voir l’attestation (nouvel onglet)"
                        className="
                          group relative block w-24 h-24 rounded-xl overflow-hidden
                          ring-1 ring-slate-200 dark:ring-slate-700
                          hover:shadow-md transition
                        "
                      >
                        <img
                          src={ILLUSTRATION}
                          alt="Illustration badge numérique"
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                        />
                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
                      </button>

                      {/* Infos */}
                      <div className="text-sm">
                        {fileName && (
                          <div className="mt-1 text-slate-600 dark:text-slate-300">
                            Fichier :{" "}
                            <span className="font-mono text-xs">{fileName}</span>
                          </div>
                        )}
                        <div className="font-medium">
                          Validé le :{" "}
                          <span className="text-slate-700 dark:text-slate-200">
                            {dateStr}
                          </span>
                        </div>
                        {clubName && (
                          <div className="mt-1 text-slate-600 dark:text-slate-300">
                            Club : <span className="font-medium">{clubName}</span>
                          </div>
                        )}
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Clique sur l’image pour ouvrir le PDF dans un nouvel onglet.
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex md:justify-end gap-2">
                        <Button
                          text="Voir le PDF"
                          variant="outline"
                          size="sm"
                          onClick={() => openPdf(badge.badge_url)}
                          hoverLabel="Ouvrir le PDF dans un nouvel onglet"
                        />
                        <Button
                          text={
                            deletingId === badge.id ? "Suppression…" : "Supprimer"
                          }
                          variant="danger"
                          size="sm"
                          loading={deletingId === badge.id}
                          onClick={() => handleDeleteBadge(badge.id)}
                          hoverLabel="Supprimer l’attestation"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Aucun badge
              <div
                className="
                  flex flex-col items-center text-center gap-4
                  p-6 rounded-xl bg-slate-50 dark:bg-slate-900/40
                  border border-dashed border-slate-300 dark:border-slate-700
                "
              >
                <img
                  src={ILLUSTRATION}
                  alt="Illustration lutte contre les dérives"
                  className="w-24 h-24 object-contain opacity-90"
                />
                <div>
                  <p className="text-slate-700 dark:text-slate-200 text-sm">
                    Aucune attestation déposée pour le moment.
                  </p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                    Ajoute un PDF et indique la date d'obtention (ainsi que ton club).
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button
                    text="Comment trouver ce document ?"
                    onClick={() => setShowHelp(true)}
                    variant="outline"
                    size="sm"
                    hoverLabel="Où récupérer le PDF sur France Judo ?"
                  />
                  <Button
                    text={showAddForm ? "Masquer le formulaire" : "Ajouter une attestation"}
                    onClick={() => setShowAddForm((s) => !s)}
                    variant="primary"
                    size="md"
                    hoverLabel={showAddForm ? "Masquer le formulaire" : "Ajouter une attestation"}
                  />
                </div>
              </div>
            )}

            {/* Formulaire d’ajout */}
            {showAddForm && (
              <form onSubmit={handleSubmitBadge} className="mt-6">
                <div
                  className="
                    grid gap-4 md:grid-cols-3
                    bg-slate-50 dark:bg-slate-900/40
                    p-4 rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70
                  "
                >
                  {/* Département */}
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Département
                    </label>
                    <select
                      value={selectedDepartement}
                      onChange={(e) => setSelectedDepartement(e.target.value)}
                      className="
                        mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700
                        bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100
                        px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500
                      "
                      required
                    >
                      <option value="">Sélectionner</option>
                      {departements.map((dep) => (
                        <option key={dep} value={dep}>
                          {dep}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Club */}
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Club
                    </label>
                    <select
                      value={selectedClubId}
                      onChange={(e) => setSelectedClubId(e.target.value)}
                      className="
                        mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700
                        bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100
                        px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500
                      "
                      required
                      disabled={!selectedDepartement}
                    >
                      <option value="">
                        {selectedDepartement
                          ? "Choisir un club"
                          : "Sélectionner un département d’abord"}
                      </option>
                      {clubsFiltered.map((club) => (
                        <option key={club.id} value={club.id}>
                          {club.nom_club}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Validé le */}
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Validé le
                    </label>
                    <input
                      type="date"
                      value={validationDate}
                      onChange={(e) => setValidationDate(e.target.value)}
                      className="
                        mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700
                        bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100
                        px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500
                      "
                      required
                    />
                  </div>

                  {/* Fichier PDF */}
                  <div className="md:col-span-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Fichier PDF de l’attestation
                      </label>
                      {/* lien aide secondaire */}
                      <button
                        type="button"
                        onClick={() => setShowHelp(true)}
                        className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                        aria-label="Aide pour trouver le document sur France Judo"
                      >
                        Comment trouver ce document ?
                      </button>
                    </div>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="
                        mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700
                        bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100
                        px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500
                      "
                      required
                    />
                    <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
                      PDF uniquement, 10&nbsp;Mo max.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                  <Button
                    text="Annuler"
                    onClick={() => setShowAddForm(false)}
                    type="button"
                    variant="subtle"
                    size="md"
                    hoverLabel="Annuler l’ajout"
                  />
                  <Button
                    text={submitting ? "Envoi..." : "Enregistrer l’attestation"}
                    type="submit"
                    disabled={submitting}
                    loading={submitting}
                    variant="primary"
                    size="md"
                    hoverLabel="Enregistrer l’attestation"
                  />
                </div>
              </form>
            )}
          </>
        )}
      </div>

      {/* =========================
          🆕 Modale d'aide France Judo
          ========================= */}
      <Modal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        size="xl"
        labelledById="help-badge-title"
        describedById="help-badge-desc"
      >
        <div className="p-4 max-h-[75vh] overflow-y-auto dark:text-slate-200">
          <h2 id="help-badge-title" className="text-lg font-semibold">
            Comment trouver et télécharger votre attestation (France Judo)
          </h2>
          <p id="help-badge-desc" className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Voici les étapes pour récupérer le PDF de votre module
            «&nbsp;Éthique, déontologie et lutte contre les violences&nbsp;».
          </p>

          <ol className="mt-4 space-y-2 text-sm list-decimal list-inside">
            <li>
              Rendez-vous sur le site <strong className="dark:text-slate-200">France Judo</strong> et ouvrez votre{" "}
              <strong className="dark:text-slate-200">Espace Licencié</strong> (connexion avec votre compte).
            </li>
            <li>
              Accédez à la rubrique <strong className="dark:text-slate-200">Mes diplômes</strong> /{" "}
              <strong className="dark:text-slate-200">Badges</strong>.
            </li>
            <li>
              Vérifiez bien que le badge <em>Module éthique / lutte contre les violences</em> est présent.
            </li>
            <li>
              Cliquez sur <strong className="dark:text-slate-200">Imprimer les badges</strong> pour obtenir le fichier PDF.
            </li>
            <li>
              Enregistrez le pdf attestation_badges_numeriques.pdf sur votre ordinateur ou téléphone.
            </li>
            <li>
              Revenez ici et déposez ce PDF avec la <strong className="dark:text-slate-200">date de validation</strong> et{" "}
              votre <strong className="dark:text-slate-200">club</strong>.
            </li>
          </ol>

          <img
            src="/img/capture_FJ_badge.png"
            alt="Exemple de l’emplacement du document sur l’espace licencié France Judo"
            className="mt-4 w-full max-h-[48vh] object-contain rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          />

          <div className="mt-6 flex justify-end gap-2">
            <Button
              text="Fermer"
              onClick={() => setShowHelp(false)}
              variant="subtle"
              size="sm"
              hoverLabel="Fermer l'aide"
            />
          </div>
        </div>
      </Modal>
    </section>
  );
}
